import React from 'react';
import { displayScore, publishedBandStyle } from '../../../services/trustBands';

/**
 * TrustGauge — FULL-circle SVG trust dial with a target marker.
 *
 * Renders identically to the Live Trust ring (a full donut filling clockwise
 * from the top) so Fleet Health on the Board Report matches the live score dial.
 * A color-banded arc shows where the score sits (strong / mid / weak), a radial
 * tick on the rim marks the org's target, and the label shows the target gap.
 *
 * Phase A7 / G307 — used in the Board Report.
 *
 * @param {Object} props
 * @param {number} props.score             — 0 to 100
 * @param {number} [props.target]          — 0 to 100; renders the target needle
 * @param {number} [props.size=200]
 * @param {string} [props.label='Fleet Health']
 */
export function TrustGauge({ score = 0, target, size = 200, label = 'Fleet Health' }) {
  // W143 — defensive numeric coercion. score may arrive as:
  //   • a clean number (the common case)
  //   • a numeric string the backend serialised that way
  //   • NaN / undefined / null when the report is still loading
  //   • a value > 100 from a stale or mis-scaled metric (e.g. an
  //     auto-create completeness score that's percentage * 1000)
  // Number() handles strings; a non-finite value falls back to 0. Reducing to a whole number is
  // essential: without it a fractional score like 36.95652 renders its decimal point as a dot that
  // disappears on some retina displays, leaving "3695652" reading as one wildly out-of-range
  // number — the bug the V2 Board Report Fleet Health screenshot caught.
  //
  // displayScore does the reducing, and it floors rather than rounds. That is the product's rule
  // and it matters most here: 84.9 rounded is 85, 85 is where Strong begins, and a dial that
  // promoted the fleet into the next band on a rounding step would be claiming something the
  // measurement does not support.
  const rawNum = Number(score);
  const safeNum = Number.isFinite(rawNum) ? rawNum : 0;
  const clamped = displayScore(safeNum) ?? 0;
  const targetClamped = target != null && Number.isFinite(Number(target))
    ? (displayScore(Number(target)) ?? null)
    : null;

  // Geometry — FULL circle donut (matches the Live Trust ring). Score fills
  // clockwise from the top (12 o'clock).
  const cx = size / 2;
  const cy = size / 2;
  const r = size * 0.38;
  const strokeWidth = size * 0.09;
  const C = 2 * Math.PI * r;
  const dash = (C * clamped) / 100;

  /* The arc is coloured by the published band — the same five-band scale, the same thresholds and
   * the same --v2-band-* tokens as every chip, pill and dial in the product.
   *
   * It used to colour by autonomyBand against the --v2-trust-band-* ramp. That is the AUTONOMY
   * tier scale (98 / 90 / 75), which answers a different question from the band: a fleet at 77 is
   * Adequate on the published scale and Restricted on the tier scale, so Fleet Health drew amber
   * while the summary beside it drew green and both were, in their own terms, correct. One
   * measurement reaches the screen, so this one follows the band.
   *
   * The consequence worth knowing: a brand palette rebinds --v2-trust-band-* to a monochrome steel
   * ramp, and this dial no longer picks that up. A score's colour carries its verdict, and a
   * verdict that goes monochrome under a theme is not a verdict. */
  const style = publishedBandStyle(clamped);

  // Target marker — a radial tick straddling the rim at `target`%, top-origin
  // clockwise (same orientation as the score fill).
  const targetMarker = targetClamped != null ? (() => {
    const ang = (-90 + (targetClamped / 100) * 360) * (Math.PI / 180);
    const inner = r - strokeWidth / 2 - 2;
    const outer = r + strokeWidth / 2 + 2;
    return {
      x1: cx + inner * Math.cos(ang), y1: cy + inner * Math.sin(ang),
      x2: cx + outer * Math.cos(ang), y2: cy + outer * Math.sin(ang),
    };
  })() : null;

  const gap = targetClamped != null ? Math.round(clamped - targetClamped) : null;

  return (
    <div className="inline-flex flex-col items-center">
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} role="img"
           aria-label={`${label}: ${clamped} out of 100, ${style.label}${targetClamped != null ? `, target ${targetClamped}` : ''}`}>
        {/* Track (full circle) */}
        <circle cx={cx} cy={cy} r={r} fill="none"
                stroke="var(--v2-surface-muted)" strokeWidth={strokeWidth} />
        {/* Score arc — clockwise from the top */}
        {clamped > 0 && (
          <circle cx={cx} cy={cy} r={r} fill="none"
                  stroke={style.color} strokeWidth={strokeWidth} strokeLinecap="round"
                  strokeDasharray={`${dash.toFixed(2)} ${(C - dash).toFixed(2)}`}
                  transform={`rotate(-90 ${cx} ${cy})`} />
        )}
        {/* Target marker */}
        {targetMarker && (
          <line x1={targetMarker.x1} y1={targetMarker.y1}
                x2={targetMarker.x2} y2={targetMarker.y2}
                stroke="var(--v2-ink)" strokeWidth={2.5} strokeLinecap="round" />
        )}
        {/* Score readout (centered in the donut hole) */}
        <text x={cx} y={cy} textAnchor="middle" dominantBaseline="central"
              fontSize={size * 0.26} fontWeight={700} fill="var(--v2-ink)">
          {clamped}
        </text>
        <text x={cx} y={cy + size * 0.16} textAnchor="middle"
              fontSize={size * 0.07} fill="var(--v2-ink-muted)" letterSpacing="0.08em">
          /100
        </text>
      </svg>

      <div className="text-center">
        <div className="text-[11.5px] uppercase tracking-wider font-semibold text-v2-ink-muted">{label}</div>
        {/* The band in words. A dial that shows only a number leaves the reader to invent their
            own thresholds, which is how a 77 gets read as a near-miss on one page and a problem
            on the next. */}
        <div className="text-[12px] font-semibold mt-0.5" style={{ color: style.ink }}>{style.label}</div>
        {targetClamped != null && (
          <div className="text-[11px] text-v2-ink-secondary mt-0.5">
            Target {targetClamped}
            {gap != null && (
              <span className={`ml-1 font-semibold ${
                gap >= 0 ? 'text-v2-success-strong' : 'text-v2-danger-strong'
              }`}>
                ({gap >= 0 ? '+' : ''}{gap})
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default TrustGauge;
